// DEADLINES STATED IN AN EMAIL — found, never acted on.
//
// A client writes "within the next 7 days" or "week commencing 10/08/26" and
// that is a date someone at ASP has to meet. Track reads it and OFFERS it; the
// human presses the button. Nothing here writes anything, and nothing here is
// allowed to guess: a pattern either matches with the anchor word ("within",
// "by", "week commencing") present, or it does not match at all. A missed
// deadline is a nuisance; an invented one on a legal file is worse.
//
// Every result carries the PHRASE it matched, so the reader can show the words
// the date came from and the person can see for themselves whether Track has
// read it correctly.
//
// Weekends are skipped for "N working days" only. Bank holidays are NOT applied
// — public/leave.js holds them, but a suggestion the human confirms does not
// need to be that precise, and pulling the table in here would couple a pure
// function to a view module.

const MONTHS = ['january', 'february', 'march', 'april', 'may', 'june', 'july',
  'august', 'september', 'october', 'november', 'december'];
const DAYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
const WORDS = { one: 1, two: 2, three: 3, four: 4, five: 5, six: 6, seven: 7,
  eight: 8, nine: 9, ten: 10, fourteen: 14, twenty: 20, thirty: 30 };

const midnight = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const addDays = (d, n) => { const x = midnight(d); x.setDate(x.getDate() + n); return x; };
const num = (s) => {
  const t = String(s || '').trim().toLowerCase();
  return /^\d+$/.test(t) ? parseInt(t, 10) : (WORDS[t] || null);
};

function addWorkingDays(from, n) {
  let d = midnight(from), left = n;
  while (left > 0) {
    d = addDays(d, 1);
    if (d.getDay() !== 0 && d.getDay() !== 6) left -= 1;
  }
  return d;
}

// A day/month with no year means the next such date on or after `from`.
function onOrAfter(from, month, day, year) {
  const base = midnight(from);
  if (year != null) return new Date(year, month, day);
  let d = new Date(base.getFullYear(), month, day);
  if (d < base) d = new Date(base.getFullYear() + 1, month, day);
  return d;
}

// A month is the word itself or a 3–4 letter abbreviation of it — NOT any word
// that happens to start the same way. "12 Marchmont Road" is an address.
function monthIndex(tok) {
  const t = String(tok || '').toLowerCase().replace(/\.$/, '');
  if (t.length < 3) return -1;
  return MONTHS.findIndex((m) => m === t || (t.length <= 4 && m.startsWith(t)));
}

function parseDayMonth(from, s) {
  let m = /^(\d{1,2})(?:st|nd|rd|th)?\s+(?:of\s+)?([a-z]+)\.?(?:\s+(\d{2,4}))?$/i.exec(s.trim());
  if (m) {
    const mi = monthIndex(m[2]);
    if (mi < 0) return null;
    let y = m[3] ? parseInt(m[3], 10) : null;
    if (y != null && y < 100) y += 2000;
    return onOrAfter(from, mi, parseInt(m[1], 10), y);
  }
  m = /^([a-z]+)\.?\s+(\d{1,2})(?:st|nd|rd|th)?(?:,?\s+(\d{2,4}))?$/i.exec(s.trim());
  if (m) {
    const mi = monthIndex(m[1]);
    if (mi < 0) return null;
    let y = m[3] ? parseInt(m[3], 10) : null;
    if (y != null && y < 100) y += 2000;
    return onOrAfter(from, mi, parseInt(m[2], 10), y);
  }
  // UK order, always: 10/08/26 is the 10th of August.
  m = /^(\d{1,2})[/.-](\d{1,2})(?:[/.-](\d{2,4}))?$/.exec(s.trim());
  if (m) {
    const day = parseInt(m[1], 10), mon = parseInt(m[2], 10) - 1;
    if (mon < 0 || mon > 11 || day < 1 || day > 31) return null;
    let y = m[3] ? parseInt(m[3], 10) : null;
    if (y != null && y < 100) y += 2000;
    return onOrAfter(from, mon, day, y);
  }
  return null;
}

// Ordered by how explicit they are: an actual date beats a relative count,
// which beats a vague "end of the week".
const RULES = [
  {
    kind: 'week commencing',
    re: /\bw(?:eek\s*)?[/.]?\s*c(?:ommencing)?\.?\s+((?:\d{1,2}[/.-]\d{1,2}(?:[/.-]\d{2,4})?)|(?:\d{1,2}(?:st|nd|rd|th)?\s+(?:of\s+)?[a-z]+\.?(?:\s+\d{2,4})?)|(?:[a-z]+\.?\s+\d{1,2}(?:st|nd|rd|th)?))/i,
    at: (from, m) => parseDayMonth(from, m[1]),
  },
  {
    kind: 'by a date',
    re: /\b(?:by|before|no later than|on or before)\s+(?:the\s+)?((?:\d{1,2}[/.-]\d{1,2}(?:[/.-]\d{2,4})?)|(?:\d{1,2}(?:st|nd|rd|th)?\s+(?:of\s+)?[a-z]+\.?(?:\s+\d{2,4})?)|(?:[a-z]+\.?\s+\d{1,2}(?:st|nd|rd|th)?))/i,
    at: (from, m) => parseDayMonth(from, m[1]),
  },
  {
    kind: 'working days',
    re: /\bwithin\s+(?:the\s+next\s+)?(\d+|[a-z]+)\s+(?:working|business)\s+days?\b/i,
    at: (from, m) => (num(m[1]) ? addWorkingDays(from, num(m[1])) : null),
  },
  {
    kind: 'days',
    re: /\bwithin\s+(?:the\s+next\s+)?(\d+|[a-z]+)\s+days?\b/i,
    at: (from, m) => (num(m[1]) ? addDays(from, num(m[1])) : null),
  },
  {
    kind: 'weeks',
    re: /\bwithin\s+(?:the\s+next\s+)?(\d+|[a-z]+)\s+weeks?\b/i,
    at: (from, m) => (num(m[1]) ? addDays(from, num(m[1]) * 7) : null),
  },
  {
    kind: 'a weekday',
    re: /\b(?:by|before)\s+(?:next\s+)?(monday|tuesday|wednesday|thursday|friday|saturday|sunday)\b/i,
    at: (from, m) => {
      const want = DAYS.indexOf(m[1].toLowerCase());
      let d = addDays(from, 1);
      while (d.getDay() !== want) d = addDays(d, 1);
      return d;
    },
  },
  {
    kind: 'end of the week',
    re: /\bby\s+(?:the\s+)?end\s+of\s+(?:the\s+)?week\b/i,
    at: (from) => { let d = midnight(from); while (d.getDay() !== 5) d = addDays(d, 1); return d; },
  },
  {
    kind: 'end of the month',
    re: /\bby\s+(?:the\s+)?end\s+of\s+(?:the\s+)?month\b/i,
    at: (from) => new Date(from.getFullYear(), from.getMonth() + 1, 0),
  },
];

/**
 * The deadline an email states, or null.
 * @param {string} text     the message body (quoted history already removed)
 * @param {Date}   received when the email arrived — every relative phrase counts from this
 * @returns {{phrase: string, date: Date, kind: string}|null}
 */
export function findDeadline(text, received) {
  const body = String(text || '');
  const from = received instanceof Date && !isNaN(received) ? received : null;
  if (!body || !from) return null;
  for (const rule of RULES) {
    const m = rule.re.exec(body);
    if (!m) continue;
    const date = rule.at(from, m);
    if (!date || isNaN(date)) continue;
    // A date in the past is a quote of some earlier deadline, not this one.
    if (date < midnight(from)) continue;
    return { phrase: m[0].trim(), date, kind: rule.kind };
  }
  return null;
}

/** "Wed 3 September" — how the suggestion is shown. */
export function longDay(d) {
  return d ? d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'long' }) : '';
}
